import React, { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function App() {
  const [startingCorpus, setStartingCorpus] = useState(1000000); // 10 Lakh in rupees
  const [monthlySaving, setMonthlySaving] = useState(0);
  const [interestRate, setInterestRate] = useState(8); // Annual interest rate
  const [years, setYears] = useState(30); // Projection period

  // Helper to format large numbers in Lakh/Crore
  const formatIndianNumber = (num) => {
    if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
    if (num >= 100000) return (num / 100000).toFixed(2) + " L";
    return num.toLocaleString();
  };

  // Generate projections
  const generateProjections = () => {
    const monthlyRate = interestRate / 12 / 100;
    let corpus = startingCorpus;
    const monthlyData = [];
    const yearlyData = [];

    const today = new Date();
    let yearStart = today.getFullYear();
    let month = today.getMonth();

    for (let m = 0; m < years * 12; m++) {
      const interest = corpus * monthlyRate;
      corpus += interest + monthlySaving;

      // Push monthly projection
      monthlyData.push({
        month: new Date(yearStart, month + m).toLocaleString("default", { month: "short", year: "2-digit" }),
        corpus: Math.round(corpus),
        diff: Math.round(interest)
      });

      // Push yearly projection at year-end (March as FY end example)
      const currentMonth = (month + m) % 12;
      if (currentMonth === 2) { // March
        const fyStart = yearStart + Math.floor((month + m) / 12);
        yearlyData.push({
          year: `${fyStart}-${(fyStart + 1).toString().slice(-2)}`,
          corpus: Math.round(corpus)
        });
      }
    }
    return { monthlyData, yearlyData };
  };

  const { monthlyData, yearlyData } = generateProjections();

  return (
    <div style={{ fontFamily: "Arial, sans-serif", padding: "20px", background: "#f5f7fa" }}>
      <h2 style={{ textAlign: "center" }}>Corpus Growth Projection</h2>

      {/* Input Controls */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "20px" }}>
        {/* Starting Corpus in Lakh */}
        <div style={{ background: "#fff", padding: "15px", borderRadius: "8px", flex: "1" }}>
          <label>
            Starting Corpus: ₹ {startingCorpus.toLocaleString()} ({(startingCorpus / 100000).toFixed(0)} Lakh)
          </label>
          <input
            type="range"
            min={10}
            max={3000}
            step={1}
            value={startingCorpus / 100000}
            onChange={(e) => setStartingCorpus(parseInt(e.target.value) * 100000)}
            style={{ width: "100%" }}
          />
          <input
            type="number"
            min={10}
            max={3000}
            step={1}
            value={startingCorpus / 100000}
            onChange={(e) => setStartingCorpus((parseInt(e.target.value) || 0) * 100000)}
            style={{ width: "100%", marginTop: "8px" }}
          />
        </div>

        {/* Monthly Saving */}
        <div style={{ background: "#fff", padding: "15px", borderRadius: "8px", flex: "1" }}>
          <label>Monthly Saving (₹)</label>
          <input
            type="number"
            value={monthlySaving}
            onChange={(e) => setMonthlySaving(parseInt(e.target.value) || 0)}
            style={{ width: "100%", marginTop: "8px" }}
          />
        </div>

        {/* Interest Rate */}
        <div style={{ background: "#fff", padding: "15px", borderRadius: "8px", flex: "1" }}>
          <label>Interest Rate: {interestRate}%</label>
          <input
            type="range"
            min={1}
            max={20}
            step={0.1}
            value={interestRate}
            onChange={(e) => setInterestRate(parseFloat(e.target.value))}
            style={{ width: "100%" }}
          />
        </div>

        {/* Years */}
        <div style={{ background: "#fff", padding: "15px", borderRadius: "8px", flex: "1" }}>
          <label>Years: {years}</label>
          <input
            type="range"
            min={1}
            max={50}
            step={1}
            value={years}
            onChange={(e) => setYears(parseInt(e.target.value))}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* Chart & Tables */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        {/* Chart */}
        <div style={{ flex: "0 0 25%", background: "#fff", padding: "10px", borderRadius: "8px" }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={yearlyData}>
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatIndianNumber} />
              <Tooltip formatter={(val) => formatIndianNumber(val)} />
              <Line type="monotone" dataKey="corpus" stroke="#8884d8" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Table */}
        <div style={{ flex: "1", background: "#fff", padding: "10px", borderRadius: "8px", overflowX: "auto" }}>
          <h3>Monthly Projection</h3>
          <table border="1" cellPadding="5" style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Corpus</th>
                <th>Difference</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((row, i) => (
                <tr key={i}>
                  <td>{row.month}</td>
                  <td>₹ {row.corpus.toLocaleString()}</td>
                  <td>₹ {row.diff.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Yearly Table */}
        <div style={{ flex: "1", background: "#fff", padding: "10px", borderRadius: "8px", overflowX: "auto" }}>
          <h3>Yearly Projection</h3>
          <table border="1" cellPadding="5" style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th>Year</th>
                <th>Corpus</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.map((row, i) => (
                <tr key={i}>
                  <td>{row.year}</td>
                  <td>₹ {row.corpus.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
